<template>
  <div class="create-screen" :class="{ 'create-screen--rtl': isRtl }">
    <!-- Header -->
    <div class="create-header">
      <button class="create-header__back" @click="goBack">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </button>
      <h1 class="create-header__title">{{ t("create_with_mia") }}</h1>
    </div>

    <!-- Content -->
    <div class="create-content">
      <!-- Step 1: Input Type Selection (if no input yet) -->
      <section v-if="!hasInput" class="create-section">
        <InputTypeSelector
          :selected-type="formData.inputType"
          @select="handleInputTypeSelect"
        />
      </section>

      <!-- Step 2: Input Preview (if has input) -->
      <section v-if="formData.inputType" class="create-section">
        <InputPreview
          :input-type="formData.inputType"
          :text-content="formData.textContent"
          :selected-file="formData.selectedFile"
          @edit="openTextModal"
          @clear="clearInput"
          @reselect="reselectFile"
        />
      </section>

      <!-- Step 3: Feature Selection -->
      <section v-if="hasInput" class="create-section">
        <FeatureSelector
          :selected-feature="formData.selectedFeature"
          @select="handleFeatureSelect"
        />
      </section>

      <!-- Step 4: Feature Configuration -->
      <section v-if="formData.selectedFeature" class="create-section">
        <FeatureConfigs
          :selected-feature="formData.selectedFeature"
          :initial-config="formData.featureData"
          @config-change="handleConfigChange"
        />
      </section>

      <!-- Step 5: Language Selection -->
      <section v-if="formData.selectedFeature" class="create-section">
        <LanguageSelector
          :selected-language="formData.language"
          @select="handleLanguageSelect"
        />
      </section>
    </div>

    <!-- Submit Button -->
    <div class="create-footer">
      <button
        class="create-submit-btn"
        :class="{ 'create-submit-btn--disabled': !canSubmit }"
        :disabled="!canSubmit || isSubmitting"
        @click="handleSubmit"
      >
        <span v-if="isSubmitting" class="create-submit-btn__spinner"></span>
        <span v-else>{{ submitButtonText }}</span>
      </button>
    </div>

    <!-- Text Input Modal -->
    <TextInputModal
      :is-open="showTextModal"
      :initial-text="formData.textContent"
      @save="handleTextSave"
      @cancel="handleTextCancel"
    />

    <!-- Hidden File Input -->
    <input
      ref="fileInputRef"
      type="file"
      :accept="fileAccept"
      style="display: none"
      @change="handleFileSelect"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useMiaStore } from "../stores/miaStore";
import { miaUploadService } from "../services/uploadService";
import { miaRefreshService } from "../services/refreshService";
import {
  MiaInputType,
  MiaFeatureType,
  MiaRefreshType,
  FileExtensions,
} from "../types";
import { useI18n } from "../i18n";

import InputTypeSelector from "../components/create/InputTypeSelector.vue";
import InputPreview from "../components/create/InputPreview.vue";
import FeatureSelector from "../components/create/FeatureSelector.vue";
import FeatureConfigs from "../components/create/FeatureConfigs.vue";
import LanguageSelector from "../components/create/LanguageSelector.vue";
import TextInputModal from "../components/create/TextInputModal.vue";

export default {
  name: "UnifiedCreateScreen",
  components: {
    InputTypeSelector,
    InputPreview,
    FeatureSelector,
    FeatureConfigs,
    LanguageSelector,
    TextInputModal,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const miaStore = useMiaStore();
    const { t, isRtl } = useI18n();

    const fileInputRef = ref(null);
    const showTextModal = ref(false);
    const isSubmitting = ref(false);

    const formData = ref({
      inputType: null,
      textContent: null,
      selectedFile: null,
      language: "auto",
      selectedFeature: null,
      featureData: {},
    });

    const hasInput = computed(() => {
      const { inputType, textContent, selectedFile } = formData.value;
      return (
        (inputType === MiaInputType.TEXT && textContent) ||
        (inputType !== MiaInputType.TEXT && selectedFile)
      );
    });

    const canSubmit = computed(() => {
      return hasInput.value && formData.value.selectedFeature !== null;
    });

    const submitButtonText = computed(() => {
      if (!hasInput.value) return t("add_content_first");
      if (!formData.value.selectedFeature) return t("select_feature");
      return t("generate");
    });

    const fileAccept = computed(() => {
      switch (formData.value.inputType) {
        case MiaInputType.IMAGE:
          return "image/*";
        case MiaInputType.AUDIO:
          return "audio/*";
        case MiaInputType.FILE:
          return ".pdf";
        default:
          return "*/*";
      }
    });

    // Initialize from route query params
    onMounted(() => {
      const { inputType, feature } = route.query;

      if (inputType) {
        formData.value.inputType = inputType;
        if (inputType === MiaInputType.TEXT) {
          showTextModal.value = true;
        } else {
          // Trigger file selection
          setTimeout(() => {
            fileInputRef.value?.click();
          }, 100);
        }
      }

      if (feature) {
        formData.value.selectedFeature = feature;
      }
    });

    const goBack = () => {
      router.back();
    };

    const handleInputTypeSelect = (type) => {
      formData.value.inputType = type;
      formData.value.textContent = null;
      formData.value.selectedFile = null;

      if (type === MiaInputType.TEXT) {
        showTextModal.value = true;
      } else {
        fileInputRef.value?.click();
      }
    };

    const openTextModal = () => {
      showTextModal.value = true;
    };

    const handleTextSave = (text) => {
      formData.value.textContent = text;
      showTextModal.value = false;
    };

    const handleTextCancel = (isEmpty) => {
      showTextModal.value = false;
      if (isEmpty && !formData.value.textContent) {
        formData.value.inputType = null;
      }
    };

    const handleFileSelect = (event) => {
      const file = event.target.files?.[0];
      if (file) {
        formData.value.selectedFile = file;
      } else if (!formData.value.selectedFile) {
        formData.value.inputType = null;
      }
      // Reset input to allow selecting the same file again
      event.target.value = "";
    };

    const reselectFile = () => {
      fileInputRef.value?.click();
    };

    const clearInput = () => {
      formData.value.inputType = null;
      formData.value.textContent = null;
      formData.value.selectedFile = null;
      formData.value.selectedFeature = null;
      formData.value.featureData = {};
    };

    const handleFeatureSelect = (feature) => {
      formData.value.selectedFeature = feature;
      formData.value.featureData = {};
    };

    const handleConfigChange = (config) => {
      formData.value.featureData = { ...formData.value.featureData, ...config };
    };

    const handleLanguageSelect = (language) => {
      formData.value.language = language;
    };

    const handleSubmit = async () => {
      if (!canSubmit.value || isSubmitting.value) return;

      isSubmitting.value = true;

      try {
        const {
          inputType,
          textContent,
          selectedFile,
          language,
          selectedFeature,
          featureData,
        } = formData.value;

        // Text input - direct API call
        if (inputType === MiaInputType.TEXT) {
          await handleTextSubmission(
            textContent,
            language,
            selectedFeature,
            featureData
          );
        } else {
          // File input - use upload service
          await handleFileSubmission(
            selectedFile,
            language,
            selectedFeature,
            featureData
          );
        }
      } catch (e) {
        console.error("[UnifiedCreateScreen] Submit error:", e);
        alert("Failed to submit. Please try again.");
      } finally {
        isSubmitting.value = false;
      }
    };

    const handleTextSubmission = async (
      content,
      language,
      feature,
      featureData
    ) => {
      let result;

      switch (feature) {
        case MiaFeatureType.PROBLEM_SOLVING:
          result = await miaStore.createProblem(
            content,
            featureData.solution_type || "step_by_step_explanation",
            language
          );
          if (result.success) {
            miaRefreshService.refresh(MiaRefreshType.PROBLEM_SOLVING);
            router.push({
              path: "/mia/problems/details",
              query: {
                problemId: result.problemId,
                solutionId: result.solutionId,
                inputType: "text",
                content: content,
              },
            });
          }
          break;

        case MiaFeatureType.FLASH_CARDS:
          result = await miaStore.createFlashCardDeck(
            content,
            featureData.question_count || 10,
            language,
            featureData.description || ""
          );
          if (result.success) {
            miaRefreshService.refresh(MiaRefreshType.FLASH_CARDS);
            router.push({
              path: "/mia/flash-cards/details",
              query: { deckId: result.deckId, deckTitle: "Flash Cards" },
            });
          }
          break;

        case MiaFeatureType.QBANK:
          result = await miaStore.createQuestionBank(
            content,
            featureData.question_count || 15,
            language
          );
          if (result.success) {
            miaRefreshService.refresh(MiaRefreshType.QBANK);
            router.push({
              path: "/mia/qbanks/details",
              query: { bankId: result.bankId },
            });
          }
          break;

        case MiaFeatureType.SUMMARY:
          const outputType = featureData.output_type || "summarize";
          const options = buildSummaryOptions(outputType, featureData);
          result = await miaStore.createSummary(
            content,
            outputType,
            language,
            options
          );
          if (result.success) {
            miaRefreshService.refresh(MiaRefreshType.SUMMARY);
            router.push({
              path: "/mia/summaries/details",
              query: { id: result.uploadId },
            });
          }
          break;
      }

      if (result && !result.success) {
        alert(result.error || "Failed to create. Please try again.");
      }
    };

    const handleFileSubmission = async (
      file,
      language,
      feature,
      featureData
    ) => {
      // Determine source_type based on file type
      const sourceType = getSourceTypeFromFile(file);

      const extraData = {
        language,
        ...buildExtraDataForFeature(feature, featureData, sourceType),
      };

      // Start background upload
      const uploadId = await miaUploadService.startUpload(
        file,
        feature,
        extraData
      );

      // Navigate back to feature list
      const routes = {
        [MiaFeatureType.PROBLEM_SOLVING]: "/mia/problems",
        [MiaFeatureType.FLASH_CARDS]: "/mia/flash-cards",
        [MiaFeatureType.QBANK]: "/mia/qbanks",
        [MiaFeatureType.SUMMARY]: "/mia/summaries",
      };

      router.push(routes[feature] || "/mia");
    };

    const buildSummaryOptions = (outputType, featureData) => {
      switch (outputType) {
        case "summarize":
          return featureData.option ? { type: featureData.option } : {};
        case "reformat":
          return featureData.option ? { style: featureData.option } : {};
        case "grammar_correction":
          return {
            include_explanations: featureData.include_explanations ?? true,
          };
        default:
          return {};
      }
    };

    /**
     * Get source_type from file based on its MIME type or extension
     * Matches Flutter's input type mapping
     */
    const getSourceTypeFromFile = (file) => {
      const mimeType = file.type || "";
      const fileName = file.name.toLowerCase();

      if (
        mimeType.startsWith("image/") ||
        /\.(jpg|jpeg|png|gif|webp|bmp)$/.test(fileName)
      ) {
        return "image";
      }
      if (
        mimeType.startsWith("audio/") ||
        /\.(mp3|wav|m4a|ogg|aac)$/.test(fileName)
      ) {
        return "audio";
      }
      if (mimeType === "application/pdf" || fileName.endsWith(".pdf")) {
        return "pdf";
      }
      return "file";
    };

    const buildExtraDataForFeature = (
      feature,
      featureData,
      sourceType = "file"
    ) => {
      switch (feature) {
        case MiaFeatureType.PROBLEM_SOLVING:
          return {
            input_type: sourceType,
            solution_type:
              featureData.solution_type || "step_by_step_explanation",
          };
        case MiaFeatureType.FLASH_CARDS:
          return {
            source_type: sourceType,
            card_count:
              featureData.question_count || featureData.card_count || 10,
            ...(featureData.description && {
              description: featureData.description,
            }),
          };
        case MiaFeatureType.QBANK:
          return {
            source_type: sourceType,
            question_count: featureData.question_count || 15,
          };
        case MiaFeatureType.SUMMARY:
          return {
            source_type: sourceType,
            output_type: featureData.output_type || "summarize",
            ...(featureData.option && {
              options: buildSummaryOptions(
                featureData.output_type,
                featureData
              ),
            }),
          };
        default:
          return {};
      }
    };

    return {
      fileInputRef,
      showTextModal,
      isSubmitting,
      formData,
      hasInput,
      canSubmit,
      submitButtonText,
      fileAccept,
      t,
      isRtl,
      goBack,
      handleInputTypeSelect,
      openTextModal,
      handleTextSave,
      handleTextCancel,
      handleFileSelect,
      reselectFile,
      clearInput,
      handleFeatureSelect,
      handleConfigChange,
      handleLanguageSelect,
      handleSubmit,
    };
  },
};
</script>

<style scoped>
.create-screen {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(135deg, #f0fff5 0%, #e6ffee 100%);
}

.create-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #c8f5d8;
  position: sticky;
  top: 0;
  z-index: 100;
}

.create-header__back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  color: #374151;
  transition: background 0.2s ease;
}

.create-header__back:hover {
  background: #e6ffee;
}

.create-header__title {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.create-content {
  flex: 1;
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
}

.create-section {
  margin-bottom: 24px;
}

.create-footer {
  padding: 16px 24px;
  background: white;
  border-top: 1px solid #c8f5d8;
  position: sticky;
  bottom: 0;
}

.create-submit-btn {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #00c444 0%, #00a83a 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 196, 68, 0.3);
}

.create-submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.4);
}

.create-submit-btn--disabled {
  background: #d1d5db;
  cursor: not-allowed;
  box-shadow: none;
}

.create-submit-btn--disabled:hover {
  transform: none;
}

.create-submit-btn__spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .create-content {
    padding: 16px;
  }
}

/* RTL Support */
.create-screen--rtl {
  direction: rtl;
  text-align: right;
}

.create-screen--rtl .create-header__back svg {
  transform: scaleX(-1);
}

/* ========== DARK MODE SUPPORT ========== */
[data-bs-theme="dark"] .create-screen,
[data-theme="dark"] .create-screen,
.dark .create-screen {
  background: linear-gradient(135deg, #0f1419 0%, #1a2332 100%);
}

[data-bs-theme="dark"] .create-header,
[data-theme="dark"] .create-header,
.dark .create-header {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .create-header__back,
[data-theme="dark"] .create-header__back,
.dark .create-header__back {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .create-header__back:hover,
[data-theme="dark"] .create-header__back:hover,
.dark .create-header__back:hover {
  background: rgba(0, 196, 68, 0.1);
}

[data-bs-theme="dark"] .create-header__title,
[data-theme="dark"] .create-header__title,
.dark .create-header__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .create-footer,
[data-theme="dark"] .create-footer,
.dark .create-footer {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .create-submit-btn--disabled,
[data-theme="dark"] .create-submit-btn--disabled,
.dark .create-submit-btn--disabled {
  background: #475569;
}
</style>
